import * as Sentry from "@sentry/react";
import { browserTracingIntegration, browserProfilingIntegration, replayIntegration } from "@sentry/react";

/**
 * Detects if the current platform is iOS/Safari
 */
const isIOSOrSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isSafari = /safari/.test(ua) && !/chrome/.test(ua);
  return isIOS || isSafari;
};

const isIndexedDBRelatedError = (event: Sentry.ErrorEvent): boolean => {
  if (!event.exception?.values) return false;
  
  return event.exception.values.some(exception => {
    const message = (exception.value || '').toLowerCase();
    const type = (exception.type || '').toLowerCase();
    
    return (
      message.includes('indexeddb') ||
      message.includes('indexed database') ||
      message.includes('idbdatabase') ||
      message.includes('connection to indexed database server lost') ||
      message.includes('internal error was encountered in the indexed database server') ||
      type.includes('indexeddb')
    );
  });
};

const isOfflineError = (event: Sentry.ErrorEvent): boolean => {
  if (!event.exception?.values) return false;
  
  return event.exception.values.some(exception => {
    const type = (exception.type || '').toLowerCase();
    const message = (exception.value || '').toLowerCase();
    
    return (
      type === 'offlineerror' ||
      message.includes('no internet connection') ||
      message.includes('network request failed')
    );
  });
};

/**
 * Expected error patterns that should be dropped before reaching Sentry.
 * Keep IndexedDB patterns out of here — they are handled by isIndexedDBRelatedError
 * so they can be enriched with context before being sent.
 */
const EXPECTED_ERROR_PATTERNS = [
  // Network-related errors — Chrome variant ("Failed to fetch")
  'auth/network-request-failed',
  'failed to fetch',

  // Invalid credentials (user typing wrong password — not a system error)
  'auth/invalid-credential',

  // Disabled Firebase account attempting to sign in — expected rejection, already toasted
  'auth/user-disabled',
] as const;

/**
 * Matches Safari/WKWebView's specific error string for a failed fetch.
 * Uses an exact value check to avoid accidentally silencing unrelated errors
 * like "Module load failed" or "Script load failed".
 */
const isIOSSafariFetchError = (event: Sentry.ErrorEvent): boolean => {
  if (!event.exception?.values) return false;
  return event.exception.values.some(
    exception =>
      exception.type === 'TypeError' &&
      (exception.value || '').toLowerCase() === 'load failed'
  );
};

const isExpectedError = (event: Sentry.ErrorEvent): boolean => {
  if (!event.exception?.values) return false;
  
  return event.exception.values.some(exception => {
    const message = (exception.value || '').toLowerCase();
    const type = (exception.type || '').toLowerCase();
    const combined = `${message} ${type}`;
        
    return EXPECTED_ERROR_PATTERNS.some(pattern => combined.includes(pattern));
  });
};

/**
 * Gets IndexedDB quota information for debugging
 */
const getIndexedDBQuota = async (): Promise<{ quota?: number; usage?: number; percentage?: number }> => {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      const quota = estimate.quota || 0;
      const usage = estimate.usage || 0;
      const percentage = quota > 0 ? Math.round((usage / quota) * 100) : 0;
      return { quota, usage, percentage };
    }
  } catch (error) {
    console.warn('Failed to get storage estimate:', error);
  }
  return {};
};

/**
 * Initialize Sentry for error tracking
 * Only enabled in production unless manually overridden
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || "development";
  const enabled =
    import.meta.env.VITE_SENTRY_ENABLED === "true" ||
    environment === "production";
  const release = import.meta.env.VITE_APP_VERSION || "dev";

  if (!dsn && enabled) {
    console.warn(
      "Sentry DSN not found but Sentry is enabled. Error tracking will not work."
    );
    return;
  }

  if (!enabled) {
    return;
  }

  console.info(
    `Initializing Sentry with release: ${release}, environment: ${environment}`
  );

  Sentry.init({
    dsn,
    integrations: [
      browserTracingIntegration(),
      browserProfilingIntegration(),
      replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    replaysSessionSampleRate: environment === "production" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: ["localhost", /^https:\/\/.*mezza\.com/],
    environment,
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
    profileSessionSampleRate: environment === "production" ? 0.1 : 1.0,
    profileLifecycle: 'trace',
    sampleRate: environment === "production" ? 0.25 : 1.0,
    sendDefaultPii: false,
    release,
    enabled,
    // Browser extension injecting chrome.runtime.sendMessage into our page.
    ignoreErrors: ["Invalid call to runtime.sendMessage"],
    beforeSend(event) {
      // Tag offline events as info and let them through — the throttle in baseApi.ts
      // (once per 30s) controls frequency. Dropping them entirely would leave us with
      // zero observability into how often users are hitting offline states.
      if (isOfflineError(event)) {
        event.fingerprint = ['offline-error', '{{ default }}'];
        event.level = 'info';
        event.tags = { ...event.tags, error_type: 'offline' };
      }

      // Drop transient network errors and expected user-behaviour errors that the
      // app already handles with retry logic or graceful fallbacks.
      if (isExpectedError(event)) return null;

      // Drop Safari/WKWebView's "Load failed" TypeError — same root cause as Chrome's
      // "Failed to fetch" but a different string. Exact match prevents silencing
      // unrelated "load failed" messages from other subsystems.
      if (isIOSSafariFetchError(event)) return null;

      // Enrich IndexedDB errors with extra context before sending — these are real
      // issues on iOS WKWebView that warrant investigation.
      if (isIndexedDBRelatedError(event)) {
        event.fingerprint = ['indexeddb-error', '{{ default }}'];
        event.level = 'warning';

        event.tags = {
          ...event.tags,
          error_type: 'indexeddb',
          is_ios_safari: isIOSOrSafari().toString(),
          document_hidden: document.hidden.toString(),
        };

        event.contexts = {
          ...event.contexts,
          browser_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            vendor: navigator.vendor,
            cookieEnabled: navigator.cookieEnabled,
          },
          visibility: {
            documentHidden: document.hidden,
            visibilityState: document.visibilityState,
          },
        };

        getIndexedDBQuota().then(quota => {
          if (quota.quota || quota.usage) {
            Sentry.setContext('storage', quota);
          }
        });
      }

      event.contexts = {
        ...event.contexts,
        connection: {
          online: navigator.onLine,
          effectiveType: (navigator as any).connection?.effectiveType,
          downlink: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
        },
      };

      event.tags = {
        ...event.tags,
        online: navigator.onLine.toString(),
      };

      return event;
    },
    // @ts-ignore TODO: fix enableLogs is not in the types
    enableLogs: true,
  });
};

/**
 * Set user context for Sentry
 * @param user User information to associate with errors or null to clear user context
 */
export const setUserContext = (
  user: { id: string; email?: string; role?: string } | null
) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      ...(user.role && { role: user.role }),
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Manually capture exception with Sentry
 * @param error Error object to capture
 * @param extras Additional context to include
 */
export const captureException = (
  error: Error,
  extras?: Record<string, any>
) => {
  Sentry.captureException(error, {
    extra: extras,
  });
};

/**
 * Add breadcrumb to Sentry
 * @param category Breadcrumb category
 * @param message Breadcrumb message
 * @param data Additional data
 */
export const addBreadcrumb = (
  category: string,
  message: string,
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: "info",
  });
};

export enum SentryMetric {
  LOGIN_EMAIL_AND_PASSWORD_SUCCESS = "login_email_and_password_success",
  LOGIN_EMAIL_AND_PASSWORD_FAILURE = "login_email_and_password_failure",
  SEND_VERIFICATION_CODE_SUCCESS = "send_verification_code_success",
  SEND_VERIFICATION_CODE_FAILURE = "send_verification_code_failure",
  VERIFY_VERIFICATION_CODE_SUCCESS = "verify_verification_code_success",
  VERIFY_VERIFICATION_CODE_FAILURE = "verify_verification_code_failure",
  FORGOT_PASSWORD_SUCCESS = "forgot_password_success",
  FORGOT_PASSWORD_FAILURE = "forgot_password_failure",
}

export const sendCountMetric = (metricName: SentryMetric, value = 1) => {
  Sentry.metrics.count(metricName, value);
};

export const sendGaugeMetric = (metricName: SentryMetric, value: number) => {
  Sentry.metrics.gauge(metricName, value);
};

export const sendDistributionMetric = (
  metricName: SentryMetric,
  value: number,
  unit: string = 'millisecond',
) => {
  Sentry.metrics.distribution(metricName, value, { unit });
};
